/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../environments/environment';
import { EncryptionService } from './encryption.service';
import { joinTrip, position, positionTracking } from '../models/socket.model';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SocketsService {
  public socket: Socket | null = null;
  private isConnected = false;
  constructor(private _encryptionService: EncryptionService) {}

  initSocket(): void {
    if (!this.socket && !this.isConnected) {
      const token = this._encryptionService.decryptLocalStorage(
        environment.storageKey
      );
      if (!token) {
        console.warn('Socket not initialized: No token found.');
        return;
      }

      this.socket = io(environment.socketUrl, {
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 3000,
        reconnectionAttempts: Infinity,
        query: { token },
        autoConnect: true,
      });

      this.setupListeners();
    }
  }

  private setupListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('Connected to WebSocket server');
      this.isConnected = true;
    });

    this.socket.on('connect_error', error => {
      console.error('WebSocket Connection Error:', error);
    });

    this.socket.on('reconnect_attempt', (attempt: number) => {
      console.log('Reconnecting... attempt', attempt);
    });

    this.socket.on('reconnect', (attemptNumber: number) => {
      console.log('Reconnected after', attemptNumber, 'attempt(s)');
      this.isConnected = true;
    });

    this.socket.on('disconnect', (reason: string) => {
      console.warn('Disconnected:', reason);
      this.isConnected = false;

      // If server forced the disconnect, attempt manual reconnect
      if (reason === 'io server disconnect') {
        // Server disconnected us: need to call connect() to reconnect
        console.log('Server forced disconnect — attempting to reconnect...');
        setTimeout(() => this.socket?.connect(), 500);
      } else if (reason === 'io client disconnect') {
        // Client-side disconnect happened somewhere else; attempt reconnect
        // only if we still have a socket instance (helps recover from accidental disconnect)
        console.log('Client disconnect detected — attempting reconnect...');
        setTimeout(() => {
          if (this.socket && !this.socket.connected) {
            this.socket.connect();
          }
        }, 1000);
      } else {
        // for network issues or other reasons, socket.io already tries to reconnect
      }
    });
  }

  trackingListner(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on('tracking', (data: any) => {
        observer.next(data);
      });
    });
  }

  userPaidEnquiry(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on('user-paid-enquiry', (data: any) => {
        observer.next(data);
      });
    });
  }

  newEnquiryListner(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on('new-enquiry', (data: any) => {
        observer.next(data);
      });
    });
  }

  autoRefreshEnquiry(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket?.on('auto-refresh', (data: any) => {
        observer.next(data);
      });
    });
  }

  sendPosition(body: position): void {
    this.socket.emit('position', body);
  }

  joinTripTracking(body: joinTrip): Observable<any> {
    return new Observable(observer => {
      this.socket.emit('join-trip', body, (response: any) => {
        observer.next(response); // Emit response
        observer.complete(); // Complete the observable
      });
    });
  }

  leaveTripTracking(body: joinTrip): Observable<any> {
    return new Observable(observer => {
      this.socket.emit('leave-trip', body, (response: any) => {
        observer.next(response); // Emit response
        observer.complete(); // Complete the observable
      });
    });
  }

  sendPositionTracking(body: positionTracking): void {
    this.socket.emit('position', body);
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners(); // Prevent memory leaks
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      console.log('Socket disconnected.');
    }
  }

  joinImmediateEnquery(id: string): Observable<any> {
    return new Observable(observer => {
      this.socket?.emit(
        'join-immediate-enquiry',
        { enquiry_id: id },
        (response: any) => {
          observer.next(response); // Emit response
          observer.complete(); // Complete the observable
        }
      );
    });
  }

  leaveImmediateEnquery(id: string): Observable<any> {
    return new Observable(observer => {
      this.socket?.emit(
        'leave-immediate-enquiry',
        { enquiry_id: id },
        (response: any) => {
          observer.next(response); // Emit response
          observer.complete(); // Complete the observable
        }
      );
    });
  }

  immediateListner(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket?.on('immediate-action', (data: any) => {
        observer.next(data);
      });
    });
  }

  messageCount(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket?.on('message-count', (data: any) => {
        observer.next(data);
      });
    });
  }

  // Enquiry Details

  joinEnquiryDetails(id: string): Observable<any> {
    return new Observable(observer => {
      this.socket?.emit(
        'join-enquiry-refresh',
        { enquiry_id: id },
        (response: any) => {
          observer.next(response); // Emit response
          observer.complete(); // Complete the observable
        }
      );
    });
  }

  leaveEnquiryDetails(id: string): Observable<any> {
    return new Observable(observer => {
      this.socket?.emit(
        'leave-enquiry-refresh',
        { enquiry_id: id },
        (response: any) => {
          observer.next(response); // Emit response
          observer.complete(); // Complete the observable
        }
      );
    });
  }

  autoRefreshEnquiryDetails(): Observable<any> {
    return new Observable<any>(observer => {
      this.socket?.on('enquiry-refresh', (data: any) => {
        observer.next(data);
      });
    });
  }
}
